import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import Dropdown from './dropdown';
const findParent = (start, root, test) => {
    let node = (start.nodeType === Node.TEXT_NODE ? start.parentNode : start);
    while (node && node !== root && !test(node))
        node = node.parentNode;
    return node === root ? null : node;
};
const isBlock = (n) => window.getComputedStyle(n).display === 'block';
// The first line typed into an empty editor has no block of its own. Wraps it in a <div>, as the
// browser does for later lines, keeping the selection.
const wrapLine = (range, root) => {
    var _a;
    const { startContainer, startOffset, endContainer, endOffset } = range;
    let top = range.commonAncestorContainer;
    for (let p = top.parentNode; p && p !== root; p = p.parentNode)
        top = p;
    const inLine = (n) => n !== null && !(n instanceof HTMLElement && isBlock(n));
    let first = top;
    while (inLine(first.previousSibling))
        first = first.previousSibling;
    let last = top;
    while (inLine(last.nextSibling))
        last = last.nextSibling;
    const line = document.createRange();
    line.setStartBefore(first);
    line.setEndAfter(last);
    const block = document.createElement('div');
    line.surroundContents(block);
    (_a = window.getSelection()) === null || _a === void 0 ? void 0 : _a.setBaseAndExtent(startContainer, startOffset, endContainer, endOffset);
    return block;
};
const unwrap = (node) => {
    const frag = document.createDocumentFragment();
    Array.from(node.childNodes).forEach(c => frag.appendChild(c));
    node.parentNode.replaceChild(frag, node);
};
const replaceAndRestoreCursor = (oldNode, newNode) => {
    oldNode.parentNode.replaceChild(newNode, oldNode);
    const sel = window.getSelection();
    if (sel) {
        sel.removeAllRanges();
        const range = document.createRange();
        range.selectNodeContents(newNode);
        range.collapse(false);
        sel.addRange(range);
    }
};
const insertAndRestoreCursor = (range, node) => {
    range.deleteContents();
    range.insertNode(node);
    const sel = window.getSelection();
    if (sel) {
        sel.removeAllRanges();
        const newRange = document.createRange();
        newRange.selectNodeContents(node);
        newRange.collapse(false);
        sel.addRange(newRange);
    }
};
const Btn = ({ icon, title, onClick, disabled, active }) => (_jsx("button", { type: "button", className: `wysiwyg-btn ${active ? 'active' : ''}`, title: title, onClick: onClick, disabled: disabled, children: _jsx("i", { className: `bi bi-${icon}` }) }));
const Wysiwyg = ({ value, onChange, onBlur, placeholder = 'Enter content...', minHeight = 200, disabled = false, className = '' }) => {
    const editorRef = React.useRef(null);
    const [isSourceMode, setIsSourceMode] = React.useState(false);
    const [history, setHistory] = React.useState([]);
    const [historyIndex, setHistoryIndex] = React.useState(-1);
    const [formatValue, setFormatValue] = React.useState(null);
    React.useEffect(() => {
        if (editorRef.current && !isSourceMode && editorRef.current.innerHTML !== value) {
            editorRef.current.innerHTML = value;
            // Apply inline styles to any existing lists that don't have them
            editorRef.current.querySelectorAll('ul, ol').forEach(list => {
                if (!list.getAttribute('style')) {
                    const type = list.tagName.toLowerCase();
                    list.setAttribute('style', `list-style-type: ${type === 'ul' ? 'disc' : 'decimal'}; margin-left: 20px; padding-left: 20px;`);
                }
                list.querySelectorAll('li').forEach(li => {
                    if (!li.getAttribute('style')) {
                        li.setAttribute('style', 'margin-left: 20px;');
                    }
                });
            });
        }
    }, [value, isSourceMode]);
    const handleInput = () => {
        if (!editorRef.current)
            return;
        onChange(editorRef.current.innerHTML);
        setHistory(prev => (prev.length >= 50 ? [...prev.slice(1), editorRef.current.innerHTML] : [...prev.slice(0, historyIndex + 1), editorRef.current.innerHTML]));
        setHistoryIndex(prev => history.length >= 50 ? 49 : prev + 1);
    };
    const withFocus = (fn) => () => { var _a; fn(); (_a = editorRef.current) === null || _a === void 0 ? void 0 : _a.focus(); };
    const handleFormatChange = (v) => {
        var _a;
        if (v) {
            (_a = editorRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            setBlockFormat(v);
            setFormatValue(null);
        }
    };
    const toggleInlineStyle = (tag) => {
        const sel = window.getSelection();
        if (!(sel === null || sel === void 0 ? void 0 : sel.rangeCount))
            return;
        const range = sel.getRangeAt(0);
        const existing = findParent(range.commonAncestorContainer, editorRef.current, n => n.tagName === tag.toUpperCase());
        if (existing)
            return unwrap(existing), handleInput();
        if (!range.toString())
            return handleInput();
        const el = document.createElement(tag);
        el.textContent = range.toString();
        insertAndRestoreCursor(range, el);
        handleInput();
    };
    const setBlockFormat = (tag) => {
        if (!editorRef.current)
            return;
        editorRef.current.focus();
        const sel = window.getSelection();
        if (!(sel === null || sel === void 0 ? void 0 : sel.rangeCount))
            return;
        const range = sel.getRangeAt(0);
        const node = findParent(range.commonAncestorContainer, editorRef.current, n => ['P', 'H1', 'H2', 'H3', 'BLOCKQUOTE', 'DIV'].includes(n.tagName));
        const el = document.createElement(tag);
        if (node) {
            el.innerHTML = node.innerHTML;
            replaceAndRestoreCursor(node, el);
        }
        else {
            el.textContent = range.toString() || '\u00A0';
            insertAndRestoreCursor(range, el);
        }
        handleInput();
    };
    const setAlignment = (align) => {
        var _a;
        const root = editorRef.current;
        const sel = window.getSelection();
        if (!root || !(sel === null || sel === void 0 ? void 0 : sel.rangeCount))
            return;
        const range = sel.getRangeAt(0);
        const start = range.commonAncestorContainer;
        if (start === root || !root.contains(start))
            return;
        const node = (_a = findParent(start, root, isBlock)) !== null && _a !== void 0 ? _a : wrapLine(range, root);
        node.style.textAlign = align;
        handleInput();
    };
    const toggleList = (type) => {
        const sel = window.getSelection();
        if (!(sel === null || sel === void 0 ? void 0 : sel.rangeCount))
            return;
        const range = sel.getRangeAt(0);
        const existing = findParent(range.commonAncestorContainer, editorRef.current, n => n.tagName === 'UL' || n.tagName === 'OL');
        if (existing) {
            const frag = document.createDocumentFragment();
            Array.from(existing.children).forEach((item) => {
                const p = document.createElement('p');
                p.innerHTML = item.innerHTML;
                frag.appendChild(p);
            });
            existing.parentNode.replaceChild(frag, existing);
            return handleInput();
        }
        const list = document.createElement(type);
        list.style.cssText = `list-style-type: ${type === 'ul' ? 'disc' : 'decimal'}; margin-left: 20px; padding-left: 20px;`;
        const li = document.createElement('li');
        li.style.marginLeft = '20px';
        li.textContent = range.toString() || 'List item';
        list.appendChild(li);
        insertAndRestoreCursor(range, list);
        handleInput();
    };
    const removeFormatting = () => {
        const sel = window.getSelection();
        if (!(sel === null || sel === void 0 ? void 0 : sel.rangeCount))
            return;
        const range = sel.getRangeAt(0);
        const text = range.toString();
        if (!text)
            return;
        range.deleteContents();
        range.insertNode(document.createTextNode(text));
        handleInput();
    };
    const isDisabled = disabled || isSourceMode;
    return (_jsxs("div", { className: `wysiwyg-editor ${disabled ? 'disabled' : ''} ${className}`, children: [_jsx("style", { children: `
        .wysiwyg-editable ul { list-style-type: disc; margin-left: 20px; }
        .wysiwyg-editable ol { list-style-type: decimal; margin-left: 20px; }
        .wysiwyg-editable li { margin-left: 20px; }
      ` }), _jsxs("div", { className: "wysiwyg-toolbar", children: [_jsxs("div", { className: "wysiwyg-toolbar-group", children: [_jsx(Btn, { icon: "type-bold", title: "Bold (Ctrl+B)", onClick: withFocus(() => toggleInlineStyle('b')), disabled: isDisabled }), _jsx(Btn, { icon: "type-italic", title: "Italic (Ctrl+I)", onClick: withFocus(() => toggleInlineStyle('i')), disabled: isDisabled }), _jsx(Btn, { icon: "type-underline", title: "Underline (Ctrl+U)", onClick: withFocus(() => toggleInlineStyle('u')), disabled: isDisabled })] }), _jsx("div", { className: "wysiwyg-divider" }), _jsxs("div", { className: "wysiwyg-toolbar-group", children: [_jsx(Btn, { icon: "text-left", title: "Align Left", onClick: withFocus(() => setAlignment('left')), disabled: isDisabled }), _jsx(Btn, { icon: "text-center", title: "Align Center", onClick: withFocus(() => setAlignment('center')), disabled: isDisabled }), _jsx(Btn, { icon: "text-right", title: "Align Right", onClick: withFocus(() => setAlignment('right')), disabled: isDisabled })] }), _jsx("div", { className: "wysiwyg-divider" }), _jsxs("div", { className: "wysiwyg-toolbar-group", children: [_jsx(Btn, { icon: "list-ul", title: "Bullet List", onClick: withFocus(() => toggleList('ul')), disabled: isDisabled }), _jsx(Btn, { icon: "list-ol", title: "Numbered List", onClick: withFocus(() => toggleList('ol')), disabled: isDisabled })] }), _jsx("div", { className: "wysiwyg-divider" }), _jsx("div", { className: "wysiwyg-toolbar-group", children: _jsx(Dropdown, { value: formatValue, onChange: handleFormatChange, disabled: isDisabled, options: Promise.resolve([
                                { value: 'p', label: 'Paragraph' },
                                { value: 'h1', label: 'Heading 1' },
                                { value: 'h2', label: 'Heading 2' },
                                { value: 'h3', label: 'Heading 3' },
                                { value: 'blockquote', label: 'Quote' }
                            ]), optionKey: x => x.value, optionLabel: x => x.label, label: "Format", size: "sm" }) }), _jsx("div", { className: "wysiwyg-divider" }), _jsx("div", { className: "wysiwyg-toolbar-group", children: _jsx(Btn, { icon: "eraser", title: "Clear Formatting", onClick: withFocus(removeFormatting), disabled: isDisabled }) }), _jsx("div", { className: "wysiwyg-spacer" }), _jsx(Btn, { icon: "code-slash", title: "View HTML Source", onClick: () => setIsSourceMode(!isSourceMode), disabled: disabled, active: isSourceMode })] }), _jsx("div", { className: "wysiwyg-content", children: isSourceMode ? (_jsx("textarea", { className: "wysiwyg-source", style: { minHeight }, value: value, onChange: e => onChange(e.target.value), disabled: disabled, spellCheck: false })) : (_jsx("div", { ref: editorRef, contentEditable: !disabled, className: "wysiwyg-editable", style: { minHeight, paddingLeft: '20px' }, onInput: handleInput, onBlur: () => { handleInput(); onBlur === null || onBlur === void 0 ? void 0 : onBlur(); }, "data-placeholder": placeholder, suppressContentEditableWarning: true })) })] }));
};
export default Wysiwyg;
